import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Badge } from './ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Plus, Edit, Trash2, GripVertical, Eye, Save, Copy } from 'lucide-react'
import { toast } from 'react-hot-toast'
import TemplateManager from './TemplateManager'
import RichTextEditor from './RichTextEditor'

const EnhancedQuoteSectionEditor = ({ sections, onSectionsChange, quoteId }) => {
  const [localSections, setLocalSections] = useState(sections || [])
  const [isEditing, setIsEditing] = useState(null)
  const [newSection, setNewSection] = useState({
    title: '',
    content: '',
    type: 'text',
    position: 0
  })
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [savedTemplates, setSavedTemplates] = useState([])

  // Load saved templates from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('quoteSectionTemplates')
      if (saved) {
        setSavedTemplates(JSON.parse(saved))
      }
    } catch (error) {
      console.error('Error loading templates:', error)
    }
  }, [])

  // Enhanced default section templates
  const defaultTemplates = [
    {
      title: 'Zahlungsbedingungen',
      content: `**Zahlungsbedingungen:**

• 30 Tage netto nach Rechnungsstellung
• Bei Aufträgen über €5.000: 50% Anzahlung bei Auftragserteilung
• Bankdaten werden mit der Rechnung übermittelt`,
      type: 'terms'
    },
    {
      title: 'Lieferzeit & Projektablauf',
      content: `**Projektablauf:**

1. **Briefing & Konzept** (1-2 Wochen)
2. **Design-Entwürfe** (2-3 Wochen)  
3. **Technische Umsetzung** (2-3 Wochen)
4. **Testing & Go-Live** (1 Woche)

*Gesamte Projektlaufzeit: 4-6 Wochen nach Auftragserteilung*`,
      type: 'delivery'
    },
    {
      title: 'Gewährleistung & Support',
      content: `**Inkludierte Leistungen:**

✓ 12 Monate Gewährleistung auf alle Arbeiten
✓ 30 Tage kostenfreier Support nach Go-Live
✓ Einweisung und Dokumentation  
✓ Kleine Anpassungen in den ersten 2 Wochen kostenfrei`,
      type: 'warranty'
    },
    {
      title: 'Was ist noch inklusive?',
      content: `**Unsere Standard-Inklusivleistungen:**

✓ **Konzepterstellung und Beratung**
✓ **Responsive Design für alle Geräte** 
✓ **Basis SEO-Optimierung**
✓ **Google Analytics Integration**
✓ **2 Wochen kostfreier Support nach Go-Live**

*Diese Leistungen sind bereits im Angebotspreis enthalten.*`,
      type: 'included'
    }
  ]

  const renderFormattedText = (content) => {
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/• /g, '<span style="color: #2563eb;">•</span> ')
      .replace(/✓ /g, '<span style="color: #16a34a; font-weight: bold;">✓</span> ')
      .replace(/(\d+)\. /g, '<strong style="color: #7c3aed;">$1.</strong> ')
      .replace(/\n/g, '<br />')
  }

  const saveSection = (section) => {
    console.log('🔍 Saving section:', section)
    console.log('🔍 Current sections:', localSections)
    
    const updatedSections = [...localSections]
    
    if (isEditing !== null) {
      updatedSections[isEditing] = section
      console.log('🔍 Updated existing section at index:', isEditing)
    } else {
      updatedSections.push({
        ...section,
        id: Date.now().toString(),
        position: localSections.length
      })
      console.log('🔍 Added new section with ID:', Date.now().toString())
    }
    
    console.log('🔍 Updated sections array:', updatedSections)
    setLocalSections(updatedSections)
    onSectionsChange(updatedSections)
    setIsEditing(null)
    setShowAddDialog(false)
    setNewSection({ title: '', content: '', type: 'text', position: 0 })
    toast.success('Abschnitt gespeichert')
  }

  const deleteSection = (index) => {
    const updatedSections = localSections.filter((_, i) => i !== index)
    setLocalSections(updatedSections)
    onSectionsChange(updatedSections)
    toast.success('Abschnitt gelöscht')
  }

  const moveSection = (index, direction) => {
    const updatedSections = [...localSections]
    const newIndex = direction === 'up' ? index - 1 : index + 1
    
    if (newIndex >= 0 && newIndex < updatedSections.length) {
      [updatedSections[index], updatedSections[newIndex]] = [updatedSections[newIndex], updatedSections[index]]
      setLocalSections(updatedSections)
      onSectionsChange(updatedSections)
    }
  }

  const saveAsTemplate = (section) => {
    const template = {
      title: section.title,
      content: section.content,
      type: section.type,
      id: Date.now().toString(),
      createdAt: new Date().toISOString()
    }
    
    const newTemplates = [...savedTemplates, template]
    setSavedTemplates(newTemplates)
    
    try {
      localStorage.setItem('quoteSectionTemplates', JSON.stringify(newTemplates))
      toast.success('Als Vorlage gespeichert')
    } catch (error) {
      toast.error('Fehler beim Speichern der Vorlage')
    }
  }

  const loadTemplate = (template) => {
    console.log('🔍 Loading template:', template)
    setNewSection({
      title: template.title,
      content: template.content,
      type: template.type,
      position: localSections.length
    })
    console.log('🔍 New section set:', {
      title: template.title,
      content: template.content.substring(0, 50) + '...',
      type: template.type
    })
    toast.success(`Vorlage "${template.title}" in Editor geladen`)
  }

  const getSectionIcon = (type) => {
    const icons = {
      'text': '📝',
      'terms': '📋',
      'delivery': '🚚',
      'warranty': '🛡️',
      'process': '⚙️',
      'included': '✅',
      'notes': '💬'
    }
    return icons[type] || '📄'
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center text-slate-800">
              📝 Angebot-Bereiche bearbeiten
            </CardTitle>
            <div className="flex items-center space-x-3">
              {/* Template Manager Popup */}
              <Dialog>
                <DialogTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="border-blue-200 text-blue-700 hover:bg-blue-50"
                  >
                    📚 Vorlagen verwalten
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Vorlagen-Verwaltung</DialogTitle>
                  </DialogHeader>
                  <TemplateManager 
                    onClose={() => {}} // Wird automatisch durch Dialog geschlossen
                    onTemplateSelect={(template) => {
                      console.log('🔍 Template selected for direct addition:', template)
                      // Add template directly to sections
                      const newSection = {
                        ...template,
                        id: Date.now().toString(),
                        position: localSections.length
                      }
                      const updatedSections = [...localSections, newSection]
                      setLocalSections(updatedSections)
                      onSectionsChange(updatedSections)
                      toast.success(`Vorlage "${template.title}" wurde hinzugefügt`)
                    }}
                  />
                </DialogContent>
              </Dialog>
              
              <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                    <Plus className="h-4 w-4 mr-2" />
                    Neuer Bereich
                  </Button>
                </DialogTrigger>
                
                {/* Dialog Content */}
                <DialogContent 
                  className="max-w-4xl max-h-[90vh] overflow-y-auto"
                  onCloseAutoFocus={(e) => {
                    e.preventDefault();
                    document.body.style.overflow = '';
                  }}
                  onEscapeKeyDown={() => {
                    document.body.style.overflow = '';
                  }}
                >
                <DialogHeader>
                  <DialogTitle>Neuen Angebot-Bereich hinzufügen</DialogTitle>
                </DialogHeader>
                
                <div className="space-y-6">
                  {/* Template Selection */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Vorlage auswählen (optional):
                    </label>
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      {defaultTemplates.map((template) => (
                        <Button
                          key={template.type}
                          size="sm"
                          variant="outline"
                          onClick={() => loadTemplate(template)}
                          className="justify-start text-left h-auto p-4"
                        >
                          <div>
                            <div className="font-medium">{getSectionIcon(template.type)} {template.title}</div>
                            <div className="text-xs text-slate-500 mt-1">
                              {template.content.replace(/[*•✓]/g, '').substring(0, 60)}...
                            </div>
                          </div>
                        </Button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Section Form */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Titel *
                      </label>
                      <Input
                        value={newSection.title}
                        onChange={(e) => setNewSection({...newSection, title: e.target.value})}
                        placeholder="z.B. Zahlungsbedingungen"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Typ
                      </label>
                      <Select
                        value={newSection.type}
                        onValueChange={(value) => setNewSection({...newSection, type: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">📝 Allgemeiner Text</SelectItem>
                          <SelectItem value="terms">📋 Zahlungsbedingungen</SelectItem>
                          <SelectItem value="delivery">🚚 Lieferzeit</SelectItem>
                          <SelectItem value="warranty">🛡️ Gewährleistung</SelectItem>
                          <SelectItem value="process">⚙️ Projektablauf</SelectItem>
                          <SelectItem value="included">✅ Inkludierte Leistungen</SelectItem>
                          <SelectItem value="notes">💬 Hinweise</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Rich Text Editor */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Inhalt * (Text-Editor mit Formatierung)
                    </label>
                    <RichTextEditor
                      value={newSection.content}
                      onChange={(content) => setNewSection({...newSection, content})}
                      placeholder="Geben Sie den Inhalt ein..."
                      height={300}
                    />
                  </div>
                  
                  {/* Live Preview */}
                  {newSection.content && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Live-Vorschau:
                      </label>
                      <div className="border border-slate-300 rounded-lg p-4 bg-slate-50">
                        <h4 className="font-semibold text-slate-800 mb-2">{newSection.title || 'Titel eingeben'}:</h4>
                        <div 
                          className="text-sm text-slate-600 leading-relaxed"
                          dangerouslySetInnerHTML={{
                            __html: renderFormattedText(newSection.content)
                          }}
                        />
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-between pt-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setShowAddDialog(false)
                        setNewSection({ title: '', content: '', type: 'text', position: 0 })
                        setIsEditing(null)
                      }}
                    >
                      Abbrechen
                    </Button>
                    <div className="space-x-2">
                      <Button
                        variant="outline"
                        onClick={() => saveAsTemplate(newSection)}
                        disabled={!newSection.title || !newSection.content}
                      >
                        <Save className="h-4 w-4 mr-2" />
                        Als Vorlage speichern
                      </Button>
                      <Button
                        onClick={() => saveSection(newSection)}
                        disabled={!newSection.title || !newSection.content}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        {isEditing !== null ? 'Aktualisieren' : 'Hinzufügen'}
                        {(!newSection.title || !newSection.content) && ' (Titel und Inhalt erforderlich)'}
                      </Button>
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {localSections.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <div className="text-4xl mb-4">📝</div>
              <p>Noch keine Angebot-Bereiche hinzugefügt</p>
              <p className="text-sm">Klicken Sie auf "Neuer Bereich" um zu starten</p>
            </div>
          ) : (
            <div className="space-y-4">
              {localSections.map((section, index) => (
                <div key={section.id || index} className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <GripVertical className="h-4 w-4 text-slate-400 cursor-move" />
                      <div>
                        <h4 className="font-semibold text-slate-800 flex items-center">
                          {getSectionIcon(section.type)} {section.title}
                        </h4>
                        <Badge variant="outline" className="text-xs mt-1">
                          {section.type}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => moveSection(index, 'up')}
                        disabled={index === 0}
                      >
                        ↑
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => moveSection(index, 'down')}
                        disabled={index === localSections.length - 1}
                      >
                        ↓
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setNewSection(section)
                          setIsEditing(index)
                          setShowAddDialog(true)
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => saveAsTemplate(section)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          if (window.confirm('Bereich wirklich löschen?')) {
                            deleteSection(index)
                          }
                        }}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="bg-slate-50 p-4 rounded border">
                    <div 
                      className="text-sm leading-relaxed"
                      dangerouslySetInnerHTML={{
                        __html: renderFormattedText(section.content)
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Enhanced Preview */}
      {localSections.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center text-slate-800">
              <Eye className="h-5 w-5 mr-2" />
              Vorschau - So erscheint es im Angebot
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6 bg-white p-6 rounded-lg border shadow-inner">
              {localSections.map((section, index) => (
                <div key={section.id || index} className="border-b border-slate-100 pb-4 last:border-b-0">
                  <h4 className="font-semibold text-slate-800 mb-3">{section.title}:</h4>
                  <div 
                    className="text-slate-600 leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: renderFormattedText(section.content)
                    }}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// Enhanced helper function for text formatting with proper column handling
const renderFormattedText = (content) => {
  let processedContent = content
  
  // Handle columns with new markers
  if (processedContent.includes('[SPALTE-START-')) {
    // Extract column count
    const columnMatch = processedContent.match(/\[SPALTE-START-(\d+)\]/);
    if (columnMatch) {
      const columnCount = parseInt(columnMatch[1]);
      
      // Extract content between markers
      const columnContent = processedContent.substring(
        processedContent.indexOf('[SPALTE-START-'),
        processedContent.indexOf('[SPALTE-END]') + '[SPALTE-END]'.length
      );
      
      // Split by column markers
      const columns = [];
      for (let i = 1; i <= columnCount; i++) {
        const marker = `**[SPALTE-${i}]**`;
        const nextMarker = i < columnCount ? `**[SPALTE-${i+1}]**` : '**[SPALTE-END]**';
        
        let startIndex = columnContent.indexOf(marker);
        let endIndex = columnContent.indexOf(nextMarker);
        
        if (startIndex !== -1) {
          startIndex += marker.length;
          if (endIndex === -1) endIndex = columnContent.indexOf('**[SPALTE-END]**');
          
          const columnText = columnContent.substring(startIndex, endIndex).trim();
          columns.push(columnText);
        }
      }
      
      // Create flex layout
      if (columns.length > 0) {
        const flexColumns = columns.map(col => 
          `<div style="flex: 1; padding: 0 10px;">${col.replace(/\n/g, '<br />')}</div>`
        ).join('');
        
        const columnsHtml = `<div style="display: flex; gap: 20px; margin: 20px 0;">${flexColumns}</div>`;
        
        // Replace column section with rendered columns
        processedContent = processedContent.replace(columnContent, columnsHtml);
      }
    }
  }
  
  return processedContent
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/• /g, '<span style="color: #2563eb;">•</span> ')
    .replace(/✓ /g, '<span style="color: #16a34a; font-weight: bold;">✓</span> ')
    .replace(/(\d+)\. /g, '<strong style="color: #7c3aed;">$1.</strong> ')
    // Links: [Text](URL) -> <a href="URL">Text</a>
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color: #2563eb; text-decoration: underline;" target="_blank">$1</a>')
    // Images: ![Alt](URL) -> <img src="URL" alt="Alt" style="max-width: 200px;">
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" style="max-width: 200px; height: auto; margin: 10px 0; border-radius: 8px;" />')
    .replace(/\n/g, '<br />')
}

export default EnhancedQuoteSectionEditor